import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { Link } from 'react-router-dom';

interface Book {
  id: number;
  title: string;
  cover_image_url: string;
  file_url: string;
  publication_year: string;
}

const AdminPanel = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalBooks, setTotalBooks] = useState(0);

  const fetchBooks = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/books?page=${page + 1}&limit=${rowsPerPage}&sortBy=download&sortOrder=desc`);
      setBooks(data.books);
      setTotalBooks(data.totalBooks);
    } catch (err) {
      console.error('Error fetching books:', err);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleClose = () => {
    setSelectedBook(null);
    setOpen(false);
  };

  const handleSave = async () => {
    try {
      if (selectedBook) {
        // Update existing book
        await axios.put(`/api/books/${selectedBook.id}`, selectedBook);
      } else {
        // Add new book
        await axios.post('/api/books', selectedBook);
      }
      fetchBooks();
      handleClose();
    } catch (err) {
      console.error('Error saving book:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`/api/books/${id}`);
      fetchBooks();
    } catch (err) {
      console.error('Error deleting book:', err);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 50));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Panel
      </Typography>
      <Box>
        <Typography variant="h6">Manage Books</Typography>
        <Button
          component={Link}
          to="/admin/books"
          variant="contained"
          color="primary"
          sx={{ mr: 2 }}
        >
          Books
        </Button>
        <Button
          component={Link}
          to="/admin/books/add"
          variant="contained"
          color="primary"
          sx={{ mr: 2 }}
        >
          Add New Book
        </Button>
        <Button
          component={Link}
          to="/admin/authors"
          variant="contained"
          color="primary"
          sx={{ mr: 2 }}
        >
          Authors
        </Button>
        <Button
          component={Link}
          to="/admin/authors/add"
          variant="contained"
          color="primary"
          sx={{ mr: 2 }}
        >
          Add New Author
        </Button>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Publication Year</TableCell>
                <TableCell>Cover Image</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {books.map((book) => (
                <TableRow key={book.id}>
                  <TableCell>{book.title}</TableCell>
                  <TableCell>{book.publication_year}</TableCell>
                  <TableCell>
                    <img src={book.cover_image_url.startsWith('http') ? book.cover_image_url : `/images/${book.cover_image_url}`} alt={book.title} style={{ width: '100px', height: 'auto' }} />
                  </TableCell>
                  <TableCell>
                    <a href={book.file_url.startsWith('http') ? book.file_url : `https://z4l1g0tev5tfrvnx.public.blob.vercel-storage.com/${book.file_url}`} target="_blank" rel="noopener noreferrer">
                      View PDF
                    </a>
                  </TableCell>
                  <TableCell>
                    <IconButton component={Link} to={`/admin/books/edit/${book.id}`}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(book.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[50, 100, 250]}
            component="div"
            count={totalBooks}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={selectedBook?.title || ''}
            onChange={(e) => setSelectedBook({ ...selectedBook!, title: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPanel; 